import { apiInstance } from './api';
import { ApiRoutes } from './consts';
import {
  BusinessMemberInterface,
  BusinessMemberPermissions,
  BusinessMemberUserInterface,
} from '@app/models/business';
import { ListResponse } from '@app/models';

export const getBusinessInfo = async () => {
  return apiInstance.get(`${ApiRoutes.Business}/`);
};

export const createBusiness = async (data: {
  name: string;
  address: string;
  category: string;
  userId: string;
  email: string;
  phoneNumber?: string;
  planName?: string;
  isTrial?: boolean;
  subcategory?: string;
  description?: string;
  websiteUrl?: string;
}) => {
  return apiInstance.post(`${ApiRoutes.Business}/save`, data);
};

export const updateBusiness = async (data: {
  name?: string;
  email?: string;
  phoneNumber?: string;
  websiteUrl?: string;
  subcategory?: string;
  address?: string;
  description?: string;
  categoryId?: string;
  tagsId?: string;
  listsId?: string;
}) => {
  return apiInstance.patch(`${ApiRoutes.Business}/update`, data);
};

export const deleteBusiness = async () => {
  return apiInstance.delete(`${ApiRoutes.Business}/delete`);
};

export const getCategories = async () => {
  return apiInstance
    .get(`${ApiRoutes.Business}/categories`)
    .then((res) => res.data)
    .catch((err) => console.error(err));
};

export const searchPlaces = async (query: string, location: string) => {
  return apiInstance.get(`${ApiRoutes.Business}/search-places`, {
    params: { query, location }
  });
};

export const uploadBusinessPhoto = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiInstance.post(`${ApiRoutes.Business}/upload-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const verifyBusinessEmail = async (email: string) => {
  return apiInstance
    .get(`${ApiRoutes.Business}/verify-email`, {
      params: { email },
    })
    .then((res) => res.data);
};

// Member Management

export interface MembersListResponse extends ListResponse<BusinessMemberInterface> {
  seatLimit: number;
  seatsUsed: number;
  seatsLeft: number;
}

export const getBusinessMember = async (
  userId: string
): Promise<BusinessMemberInterface> => {
  const response = await apiInstance.get(
    `${ApiRoutes.Business}/members/${userId}`
  );
  return response.data;
};

export const listBusinessMembers = async (
  page: number = 1,
  perPage: number = 10,
  search?: string,
  sortField?: string,
  sortDirection?: 'asc' | 'desc'
): Promise<MembersListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
  });
  if (search) {
    params.append('search', search);
  }
  if (sortField) {
    params.append('sortBy', sortField);
    params.append('sortOrder', sortDirection || 'asc');
  }
  const response = await apiInstance.get(
    `${ApiRoutes.Business}/members?${params.toString()}`
  );
  return response.data;
};

export const searchUsersForInvite = async (
  search: string,
  limit: number = 10
): Promise<BusinessMemberUserInterface[]> => {
  const params = new URLSearchParams({
    search,
    limit: limit.toString(),
  });
  const response = await apiInstance.get(
    `${ApiRoutes.Business}/members/search-users?${params.toString()}`
  );
  return response.data;
};

export const addBusinessMember = async (
  userId: string,
  permissions?: BusinessMemberPermissions | null,
  jobTitle?: string
): Promise<{ message: string; member: BusinessMemberInterface }> => {
  const response = await apiInstance.post(`${ApiRoutes.Business}/members`, {
    userId,
    permissions,
    jobTitle,
  });
  return response.data;
};

export const removeBusinessMember = async (
  userId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiInstance.delete(
    `${ApiRoutes.Business}/members/${userId}`
  );
  return response.data;
};

export const updateBusinessMember = async (
  userId: string,
  data: {
    permissions?: BusinessMemberPermissions | null;
    jobTitle?: string | null;
  }
): Promise<{ message: string; member: BusinessMemberInterface }> => {
  const response = await apiInstance.put(
    `${ApiRoutes.Business}/members/${userId}`,
    data
  );
  return response.data;
};

export const transferBusinessOwnership = async (
  newOwnerId: string
): Promise<{ message: string; business: unknown }> => {
  const response = await apiInstance.put(
    `${ApiRoutes.Business}/members/transfer-ownership`,
    { newOwnerId }
  );
  return response.data;
};

export const getCurrentUserPermissions = async (): Promise<{
  isOwner: boolean;
  permissions: BusinessMemberPermissions | null;
}> => {
  const response = await apiInstance.get(
    `${ApiRoutes.Business}/members/me`
  );
  return response.data;
};

// Invite Management

export interface BusinessInviteInterface {
  id: string;
  businessId: string;
  email: string;
  jobTitle?: string | null;
  permissions?: BusinessMemberPermissions | null;
  token: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED';
  expiresAt: string;
  createdBy: string;
  memberId: string;
  createdAt: string;
  updatedAt: string;
  creator?: {
    firstName: string;
    lastName: string;
  };
  business?: {
    id: string;
    name: string;
  };
}

export interface InvitesListResponse {
  data: BusinessInviteInterface[];
  total: number;
  page: number;
  perPage: number;
}

export const createBusinessInvite = async (
  email: string,
  jobTitle?: string,
  permissions?: BusinessMemberPermissions | null
): Promise<{ message: string; invite: BusinessInviteInterface }> => {
  const response = await apiInstance.post(
    `${ApiRoutes.Business}/members/invites`,
    { email, jobTitle, permissions }
  );
  return response.data;
};

export const listBusinessInvites = async (
  page: number = 1,
  perPage: number = 10
): Promise<InvitesListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
  });
  const response = await apiInstance.get(
    `${ApiRoutes.Business}/members/invites?${params.toString()}`
  );
  return response.data;
};

export const resendBusinessInvite = async (
  inviteId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiInstance.post(
    `${ApiRoutes.Business}/members/invites/${inviteId}/resend`
  );
  return response.data;
};

export const cancelBusinessInvite = async (
  inviteId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiInstance.delete(
    `${ApiRoutes.Business}/members/invites/${inviteId}`
  );
  return response.data;
};

export const getInviteByToken = async (
  token: string
): Promise<BusinessInviteInterface> => {
  const response = await apiInstance.get(
    `${ApiRoutes.Business}/invites/${token}`
  );
  return response.data;
};

export const acceptBusinessInvite = async (
  token: string
): Promise<{ message: string; member: BusinessMemberInterface }> => {
  const response = await apiInstance.post(
    `${ApiRoutes.Business}/invites/${token}/accept`
  );
  return response.data;
};

export const declineBusinessInvite = async (
  token: string
): Promise<{ message: string }> => {
  const response = await apiInstance.post(
    `${ApiRoutes.Business}/invites/${token}/decline`
  );
  return response.data;
};
